class SideDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]') || event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
  }

  open() {
    if (this.dialog.open) return;
    const header = document.querySelector('sticky-header');
    if (header) {
      const rect = header.getBoundingClientRect();
      this.dialog.style.setProperty('--drawer-header-top', `${Math.max(rect.top, 0)}px`);
      this.dialog.style.setProperty('--drawer-header-height', `${rect.height}px`);
    }
    this.dialog.showModal();
    this.dialog.classList.remove('is-closing');
    this.dialog.querySelector('[data-drawer-close]')?.focus();
  }

  close() {
    if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;
    this.dialog.classList.add('is-closing');
    const finish = () => {
      this.dialog.classList.remove('is-closing');
      this.dialog.close();
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
    else this.dialog.addEventListener('animationend', finish, { once: true });
  }
}

if (!customElements.get('side-drawer')) customElements.define('side-drawer', SideDrawer);

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-drawer-open]');
  if (!trigger) return;
  const drawer = document.getElementById(`Drawer-${trigger.dataset.drawerOpen}`);
  if (!drawer) return;
  event.preventDefault();
  document.querySelectorAll('details[data-disclosure][open]').forEach((details) => (details.open = false));
  drawer.open();
});

/* ---------- Cart: drawer and /cart page share this code ---------- */

const cartDrawer = () => document.getElementById('Drawer-cart');
const cartRoots = () => [...document.querySelectorAll('[data-cart-root]')];
const cartSections = () => cartRoots().map((root) => root.dataset.section);
const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

/* Money, formatted like Shopify from a sample the server renders (e.g. "€12.345,67"). */
let moneyParts;
function formatMoney(cents) {
  if (!moneyParts) {
    const sample = cartRoots()[0]?.dataset.moneySample || '';
    const match = sample.match(/^(.*?)(\d[\d.,\s '’]*\d)(.*)$/);
    const number = match ? match[2] : '12,345.67';
    const decimals = /[.,]\d{2}$/.test(number) ? 2 : 0;
    moneyParts = {
      prefix: match ? match[1] : '',
      suffix: match ? match[3] : '',
      decimals,
      decimal: decimals ? number.slice(-3, -2) : '',
      thousands: /\D/.test(number.slice(2, 3)) ? number.slice(2, 3) : '',
    };
  }
  const { prefix, suffix, decimals, decimal, thousands } = moneyParts;
  const value = (Math.max(0, cents) / 100).toFixed(decimals);
  const [whole, fraction] = value.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  return `${prefix}${grouped}${fraction ? decimal + fraction : ''}${suffix}`;
}

function updateBubbles(count) {
  document.querySelectorAll('[data-cart-count]').forEach((bubble) => {
    const number = bubble.querySelector('[aria-hidden="true"]');
    if (number) number.textContent = count;
    bubble.hidden = count === 0;
  });
}

function syncBubbles(root) {
  const countSource = root?.querySelector('[data-cart-count-source]');
  const count = Number(root?.querySelector('[data-cart-item-count]')?.textContent || 0);
  document.querySelectorAll('[data-cart-count]').forEach((bubble) => {
    if (countSource) bubble.replaceChildren(countSource.content.cloneNode(true));
    bubble.hidden = count === 0;
  });
}

/* Full swap of one container. */
function renderRoot(root, html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const fresh = doc.querySelector('[data-cart-drawer-content]');
  const current = root.querySelector('[data-cart-drawer-content]');
  if (!fresh || !current) return;
  // Keep the photos already on screen (same product), so nothing flashes empty while they reload.
  const mediaKey = (link) => {
    try {
      return `${link.closest('.cart-item') ? 'line' : 'card'}:${new URL(link.getAttribute('href'), location.origin).pathname}`;
    } catch {
      return '';
    }
  };
  const loaded = new Map();
  current.querySelectorAll('.cart-item__media, .cart-suggestion__media').forEach((link) => {
    const img = link.querySelector('img');
    if (img?.complete && img.naturalWidth) loaded.set(mediaKey(link), img);
  });
  fresh.querySelectorAll('.cart-item__media, .cart-suggestion__media').forEach((link) => {
    const old = loaded.get(mediaKey(link));
    const img = link.querySelector('img');
    if (!old || !img) return;
    old.className = img.className;
    img.replaceWith(old);
  });
  current.replaceWith(fresh);
}

/* Light update: only the numbers change, so images don't reload and scroll stays.
   Falls back to a full swap when lines were added or removed. */
function patchRoot(root, html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const freshLines = [...doc.querySelectorAll('.cart-item')];
  // A line drawn before the server answered (instant add) becomes the server's version of that
  // line only; the lines next to it are not touched.
  const taken = new Set([...root.querySelectorAll('.cart-item:not(.is-pending)')].map((line) => line.dataset.key));
  root.querySelectorAll('.cart-item.is-pending').forEach((line) => {
    const id = String(line.dataset.key).split(':')[0];
    const match = freshLines.find((fresh) => fresh.dataset.key.startsWith(`${id}:`) && !taken.has(fresh.dataset.key));
    if (!match) return;
    taken.add(match.dataset.key);
    const node = match.cloneNode(true);
    const img = line.querySelector('.cart-item__media img');
    const slot = node.querySelector('.cart-item__media img');
    if (img?.complete && img.naturalWidth && slot) slot.replaceWith(img);
    line.replaceWith(node);
  });
  const currentLines = [...root.querySelectorAll('.cart-item:not(.is-removing)')];
  const freshByKey = new Map(freshLines.map((line) => [line.dataset.key, line]));
  const unmatchedCurrent = currentLines.filter((line) => !freshByKey.has(line.dataset.key));
  const currentKeys = new Set(currentLines.map((line) => line.dataset.key));
  const unmatchedFresh = freshLines.filter((line) => !currentKeys.has(line.dataset.key));
  // A size swap keeps the line where it is; only its id changes.
  if (unmatchedCurrent.length === 1 && unmatchedFresh.length === 1) {
    unmatchedCurrent[0].dataset.key = unmatchedFresh[0].dataset.key;
    freshByKey.set(unmatchedFresh[0].dataset.key, unmatchedFresh[0]);
  }
  const sameShape =
    freshLines.length > 0 && freshLines.length === currentLines.length && currentLines.every((line) => freshByKey.has(line.dataset.key));

  if (!sameShape) {
    const body = root.querySelector('.cart-drawer__body');
    const scroll = body?.scrollTop || 0;
    const addedBox = root.querySelector('[data-cart-added]');
    const addedText = addedBox && !addedBox.hidden ? addedBox.querySelector('[data-cart-added-text]')?.textContent : '';
    renderRoot(root, html);
    const newBody = root.querySelector('.cart-drawer__body');
    if (newBody) newBody.scrollTop = scroll;
    const box = root.querySelector('[data-cart-added]');
    if (addedText && box) {
      box.querySelector('[data-cart-added-text]').textContent = addedText;
      box.hidden = false;
    }
    return;
  }

  currentLines.forEach((line) => {
    const fresh = freshByKey.get(line.dataset.key);
    Object.assign(line.dataset, {
      line: fresh.dataset.line,
      quantity: fresh.dataset.quantity,
      max: fresh.dataset.max,
      unitPrice: fresh.dataset.unitPrice,
      unitCompare: fresh.dataset.unitCompare,
    });
    ['.cart-item__price', '.quantity', '[data-line-qty]', '[data-line-size]', '[data-cart-sizes]', '.cart-item__media'].forEach((selector) => {
      if (selector === '[data-cart-sizes]' || selector === '.cart-item__media') {
        // Keep the open size list and the loaded image; only sync which size is current.
        if (selector === '[data-cart-sizes]') {
          const freshSizes = fresh.querySelector(selector);
          line.querySelectorAll('[data-cart-swap]').forEach((button) => {
            const match = freshSizes?.querySelector(`[data-cart-swap="${button.dataset.cartSwap}"]`);
            if (!match) return;
            button.disabled = match.disabled;
            button.classList.toggle('is-current', match.classList.contains('is-current'));
            if (match.hasAttribute('aria-current')) button.setAttribute('aria-current', 'true');
            else button.removeAttribute('aria-current');
          });
        }
        return;
      }
      const next = fresh.querySelector(selector);
      if (next) line.querySelector(selector)?.replaceWith(next);
    });
  });
  root.querySelectorAll('.cart-item.is-removing').forEach((line) => line.remove());

  ['.cart-drawer__shipping', '.cart-drawer__totals', '[data-cart-note]', '[data-cart-title]', '[data-cart-count-source]', '[data-cart-item-count]', '[data-cart-discount]'].forEach(
    (selector) => {
      const next = doc.querySelector(selector);
      const current = root.querySelector(selector);
      if (!next || !current) return;
      // The discount field stays open if the customer had opened it.
      const toggle = next.querySelector('[data-cart-discount-toggle]');
      if (toggle && current.querySelector('[data-cart-discount-toggle]')?.open) toggle.open = true;
      current.replaceWith(next);
    }
  );
}

/* Apply a server answer to every cart on the page: the one the customer used gets the
   light patch, the others (e.g. the closed drawer on /cart) a full swap. */
function applySections(sections, origin, added) {
  if (!sections) return;
  cartRoots().forEach((root) => {
    const html = sections[root.dataset.section];
    if (!html) return;
    if (root === origin) patchRoot(root, html);
    else renderRoot(root, html);
  });
  const drawer = cartDrawer();
  if (added && drawer) showAdded(drawer, added);
  syncCheckoutBusy();
  syncBubbles(origin || drawer || cartRoots()[0]);
}

// "Added to your cart · Berlin Vivid Orange · EU 39 · Qty 1" after an add to cart.
function showAdded(root, item) {
  const box = root.querySelector('[data-cart-added]');
  const text = box?.querySelector('[data-cart-added-text]');
  if (!box || !text) return;
  const size = (item.options_with_values || []).find((option) => /size|größe|veli|méret/i.test(option.name));
  const parts = [item.product_title];
  if (size) parts.push(`${root.dataset.sizeSystem} ${String(size.value).split('-')[0]}`);
  parts.push(root.dataset.qtyLabel.replace('__QTY__', item.quantity));
  text.textContent = parts.join(' · ');
  box.hidden = false;
}

function showCartError(root, message) {
  const error = root?.querySelector('[data-cart-error]');
  if (!error) return;
  error.textContent = message;
  error.hidden = false;
}

// When the server disagrees (e.g. stock changed), show its version of every cart.
async function refreshCart() {
  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=${cartSections().join(',')}`);
    applySections(await response.json(), null);
  } catch {
    cartRoots().forEach((root) => root.querySelector('.cart-drawer__items')?.removeAttribute('aria-busy'));
  }
}

/* ---------- Stale page guard ---------- */

// Prefetched pages (e.g. an SEO app's link preloading) and back/forward cache restores show the cart
// as it was when that page was fetched. Ask the server for the real cart and redraw when it differs.
async function checkCartIsCurrent() {
  if (!/(^|; )cart=/.test(document.cookie)) return;
  const marker = document.querySelector('[data-cart-item-count]');
  if (!marker) return;
  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
    const cart = await response.json();
    const count = Number(marker.textContent) || 0;
    const subtotal = Number(marker.dataset.subtotal);
    if (cart.item_count !== count || (Number.isFinite(subtotal) && cart.items_subtotal_price !== subtotal)) refreshCart();
  } catch {}
}
checkCartIsCurrent();
// A page rendered ahead (speculation rules) may have been prepared before the last cart change.
document.addEventListener('prerenderingchange', checkCartIsCurrent);
window.addEventListener('pageshow', (event) => {
  // Back from checkout: the page comes back exactly as it was left, spinner included. Reset it.
  document.querySelectorAll('.cart-drawer__checkout[aria-busy]').forEach((button) => button.removeAttribute('aria-busy'));
  document.querySelectorAll('.cart-drawer__form[data-cart-ready]').forEach((form) => delete form.dataset.cartReady);
  if (event.persisted) checkCartIsCurrent();
});

/* ---------- Instant quantity changes ---------- */

function recalcRoot(root) {
  const lines = [...root.querySelectorAll('.cart-item:not(.is-removing)')];
  let subtotal = 0;
  let count = 0;
  lines.forEach((line) => {
    const quantity = Number(line.dataset.quantity) || 0;
    subtotal += quantity * (Number(line.dataset.unitPrice) || 0);
    count += quantity;
  });

  const title = root.querySelector('[data-cart-title]');
  if (title && root.dataset.titleTemplate) title.textContent = root.dataset.titleTemplate.replace('__COUNT__', count);
  const subtotalEl = root.querySelector('[data-cart-subtotal]');
  if (subtotalEl) subtotalEl.textContent = formatMoney(subtotal);
  const note = root.querySelector('[data-cart-note]');
  if (note && root.dataset.noteTemplate) note.textContent = root.dataset.noteTemplate.replace('__AMOUNT__', formatMoney(subtotal));

  const threshold = Number(root.dataset.threshold) || 0;
  const shipTitle = root.querySelector('[data-ship-title]');
  const shipBar = root.querySelector('[data-ship-bar]');
  if (threshold > 0 && shipTitle) {
    // Free shipping is for orders strictly over the threshold.
    const remaining = threshold - subtotal + 1;
    shipTitle.textContent =
      remaining > 0 ? root.dataset.shipRemaining.replace('__AMOUNT__', formatMoney(remaining)) : root.dataset.shipReached;
    if (shipBar) shipBar.style.width = `${Math.min(100, (subtotal * 100) / threshold)}%`;
  }

  updateBubbles(count);
}

// "Only 5 items were added to your cart due to availability." under the line that hit its stock.
function showStockNotice(root, line, max) {
  if (!line || !max) return;
  let notice = line.querySelector('[data-stock-notice]');
  if (!notice) {
    notice = document.createElement('p');
    notice.className = 'cart-item__notice';
    notice.dataset.stockNotice = '';
    notice.setAttribute('role', 'status');
    (line.querySelector('.cart-item__details') || line).append(notice);
  }
  notice.textContent = max === 1 ? root.dataset.stockOne : (root.dataset.stockMany || '').replace('__COUNT__', max);
}

function setLineQuantity(root, line, quantity) {
  line.dataset.quantity = quantity;
  if (quantity === 0) {
    line.classList.add('is-removing');
  } else {
    const max = Number(line.dataset.max) || 0;
    const value = line.querySelector('.quantity__value');
    if (value) value.textContent = quantity;
    const [minus, plus] = line.querySelectorAll('.quantity [data-cart-quantity]');
    if (minus) minus.dataset.cartQuantity = quantity - 1;
    if (plus) plus.dataset.cartQuantity = quantity + 1;
    if (!(max > 0 && quantity >= max)) line.querySelector('[data-stock-notice]')?.remove();
    const qtyLabel = line.querySelector('[data-line-qty]');
    if (qtyLabel) qtyLabel.textContent = root.dataset.qtyLabel.replace('__QTY__', quantity);
    const price = line.querySelector('[data-line-price]');
    if (price) price.textContent = formatMoney(quantity * Number(line.dataset.unitPrice));
    const compare = line.querySelector('[data-line-compare]');
    if (compare) compare.textContent = formatMoney(quantity * Number(line.dataset.unitCompare));
  }
  root.querySelector('[data-cart-error]')?.setAttribute('hidden', '');
  recalcRoot(root);
}

// Clicks are batched per line; requests run one after another so they never race.
const pendingLines = new Map();
let cartQueue = Promise.resolve();
// Requests waiting in or running through the queue. The cart is redrawn only when this is back to 0,
// so an older answer (qty 2) is never drawn over a newer click (qty 3).
let queuedCartOps = 0;
// Other cart requests (size swap, discount codes) that checkout must also wait for.
let otherCartOps = 0;
const trackCart = async (work) => {
  otherCartOps += 1;
  syncCheckoutBusy();
  try {
    return await work();
  } finally {
    otherCartOps -= 1;
    syncCheckoutBusy();
  }
};

// While any cart request is on its way (quantity, add, size swap, code), "Continue to checkout"
// shows its spinner and can't be tapped, and the discount/total figures are dimmed: they are
// only right again once Shopify has answered.
const cartBusy = () => queuedCartOps > 0 || pendingLines.size > 0 || otherCartOps > 0;
function syncCheckoutBusy() {
  const busy = cartBusy();
  cartRoots().forEach((root) => {
    root.querySelectorAll('.cart-drawer__checkout').forEach((button) => {
      if (busy) button.setAttribute('aria-busy', 'true');
      else if (!button.form?.dataset.cartReady) button.removeAttribute('aria-busy');
    });
    root.querySelector('.cart-drawer__totals')?.classList.toggle('is-stale', busy);
  });
}

// A line's key can change when Shopify adjusts the line (e.g. caps it to the stock left), so a
// change is sent with the key that line carries now: same variant, current key.
function liveKey(root, key) {
  if (root.querySelector(`.cart-item[data-key="${CSS.escape(key)}"]`)) return key;
  const id = String(key).split(':')[0];
  return root.querySelector(`.cart-item[data-key^="${CSS.escape(id)}:"]`)?.dataset.key || key;
}

function queueLineChange(root, key, quantity) {
  const pending = pendingLines.get(key);
  clearTimeout(pending?.timer);
  // Removing is sent at once; + / − wait a moment so fast clicks become one request.
  const delay = quantity === 0 ? 0 : 250;
  pendingLines.set(key, {
    root,
    quantity,
    timer: setTimeout(() => {
      pendingLines.delete(key);
      queuedCartOps += 1;
      syncCheckoutBusy();
      cartQueue = cartQueue.then(() => sendLineChange(root, key, quantity)).finally(() => {
        queuedCartOps -= 1;
        syncCheckoutBusy();
      });
    }, delay),
  });
  syncCheckoutBusy();
}

async function sendLineChange(root, key, quantity, retried = false) {
  const id = liveKey(root, key);
  try {
    const response = await fetch(`${root.dataset.cartChangeUrl}.js`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ id, quantity, sections: cartSections() }),
    });
    const data = await response.json();
    if (!response.ok) {
      // The line was renamed under us (its key changed): fetch the cart as it is and send once more.
      if (!retried && /id or line/i.test(`${data.description || ''} ${data.message || ''}`)) {
        await refreshCart();
        const fresh = liveKey(root, key);
        if (fresh !== id) return sendLineChange(root, fresh, quantity, true);
      }
      throw new Error(data.description || root.dataset.error);
    }
    // Only sync once the customer has stopped clicking, so numbers never jump back.
    if (pendingLines.size === 0 && queuedCartOps === 1) applySections(data.sections, root);
  } catch (error) {
    await refreshCart();
    showCartError(root, error.message || root.dataset.error);
  }
}

// Leaving the page or closing the drawer sends any change still waiting, so nothing is lost.
function flushLineChanges() {
  pendingLines.forEach(({ root, quantity, timer }, key) => {
    clearTimeout(timer);
    fetch(`${root.dataset.cartChangeUrl}.js`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ id: key, quantity }),
      keepalive: true,
    });
  });
  pendingLines.clear();
}
window.addEventListener('pagehide', flushLineChanges);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') flushLineChanges();
});
document.addEventListener('click', (event) => {
  if (event.target.closest('#Drawer-cart [data-drawer-close]')) flushLineChanges();
});

/* ---------- Checkout waits for the cart ---------- */

// The cart shows a new pair at once, but Shopify confirms it a moment later. A tap on "Continue to
// checkout" in between would reach Shopify with the old (often empty) cart, which sends the
// shopper to the home page. So the button waits until every add and change has been saved.
async function cartSettled() {
  // Quantity changes still waiting for their short pause are sent now.
  pendingLines.forEach(({ root, quantity, timer }, key) => {
    clearTimeout(timer);
    pendingLines.delete(key);
    queuedCartOps += 1;
    cartQueue = cartQueue.then(() => sendLineChange(root, key, quantity)).finally(() => (queuedCartOps -= 1));
  });
  const deadline = Date.now() + 15000;
  while ((queuedCartOps > 0 || pendingLines.size > 0 || otherCartOps > 0) && Date.now() < deadline) {
    await cartQueue.catch(() => {});
    await new Promise((resolve) => setTimeout(resolve, 40));
  }
}

// Pairs the shopper sees in this cart (lines being removed don't count).
function shownItemCount(root) {
  return [...(root?.querySelectorAll('.cart-item:not(.is-removing)') || [])].reduce((sum, line) => sum + (Number(line.dataset.quantity) || 0), 0);
}

async function shopifyCart() {
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(response.status);
  return response.json();
}

// "Continue to checkout" never leaves before Shopify's cart holds exactly what the shopper sees:
// it waits for every add, quantity change, size swap and discount, then compares the pair count.
// If they still differ after a few seconds, the cart is redrawn from Shopify and nothing is submitted.
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.classList.contains('cart-drawer__form')) return;
  if (form.dataset.cartReady === 'true') {
    delete form.dataset.cartReady;
    return;
  }
  event.preventDefault();
  const button = event.submitter || form.querySelector('[name="checkout"]');
  if (button?.getAttribute('aria-busy') === 'true') return;
  button?.setAttribute('aria-busy', 'true');
  const root = form.closest('[data-cart-root]') || cartDrawer();

  let ready = false;
  try {
    await cartSettled();
    const deadline = Date.now() + 8000;
    while (Date.now() < deadline) {
      const cart = await shopifyCart();
      const shown = shownItemCount(root);
      if (cart.item_count > 0 && cart.item_count === shown) {
        ready = true;
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, 350));
      await cartSettled();
    }
  } catch {
    // The check itself failed (network): let Shopify's checkout decide.
    ready = true;
  }

  if (!ready) {
    // Show the shopper Shopify's real cart instead of a checkout with different pairs.
    await refreshCart();
    showCartError(root, root?.dataset.error);
    button?.removeAttribute('aria-busy');
    return;
  }
  form.dataset.cartReady = 'true';
  if (button && form.requestSubmit) form.requestSubmit(button);
  else form.submit();
});

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-cart-root] [data-cart-quantity]');
  if (!button || button.disabled) return;
  const root = button.closest('[data-cart-root]');
  const line = button.closest('.cart-item');
  if (!root || !line?.dataset.key) return;
  const max = Number(line.dataset.max) || 0;
  const quantity = Math.max(0, Number(button.dataset.cartQuantity));
  if (max > 0 && quantity > max) {
    showStockNotice(root, line, max);
    return;
  }
  setLineQuantity(root, line, quantity);
  queueLineChange(root, line.dataset.key, quantity);
  if (quantity === 0 && !root.querySelector('.cart-item:not(.is-removing)')) {
    const empty = root.querySelector('template[data-cart-empty-state]');
    const body = root.querySelector('.cart-drawer__body');
    if (empty && body) body.replaceWith(empty.content.firstElementChild.cloneNode(true));
  }
});

/* ---------- Edit size ---------- */

// "Edit size ↓" shows the other sizes of that pair right in the cart.
// Suggestions row: the arrows scroll one card at a time; each end greys its arrow out.
document.addEventListener('click', (event) => {
  const arrow = event.target.closest('[data-suggest-scroll]');
  if (!arrow) return;
  const list = arrow.closest('.cart-suggestions')?.querySelector('[data-suggest-list]');
  const card = list?.firstElementChild;
  if (!card) return;
  const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(list).columnGap || 0);
  list.scrollBy({ left: step * Number(arrow.dataset.suggestScroll), behavior: 'smooth' });
});

const updateSuggestArrows = (list) => {
  const box = list.closest('.cart-suggestions');
  if (!box) return;
  const atStart = list.scrollLeft <= 1;
  const atEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 1;
  box.querySelector('[data-suggest-scroll="-1"]')?.toggleAttribute('disabled', atStart);
  box.querySelector('[data-suggest-scroll="1"]')?.toggleAttribute('disabled', atEnd);
};

document.addEventListener(
  'scroll',
  (event) => {
    if (event.target instanceof Element && event.target.matches('[data-suggest-list]')) updateSuggestArrows(event.target);
  },
  true
);

// The cart re-renders its contents; watch just the drawer (and the cart page root), not the whole page.
document.querySelectorAll('#Drawer-cart, [data-cart-root]').forEach((root) => {
  new MutationObserver(() => {
    root.querySelectorAll('[data-suggest-list]').forEach(updateSuggestArrows);
  }).observe(root, { childList: true, subtree: true });
});

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-cart-root] [data-cart-edit]');
  if (!toggle) return;
  const sizes = toggle.closest('.cart-item')?.querySelector('[data-cart-sizes]');
  if (!sizes) return;
  sizes.hidden = !sizes.hidden;
  toggle.setAttribute('aria-expanded', String(!sizes.hidden));
});

// Picking a size swaps the line: add the new size first, then remove the old line,
// so nothing is lost if the new size can't be added.
document.addEventListener('click', async (event) => {
  const option = event.target.closest('[data-cart-root] [data-cart-swap]');
  if (!option || option.getAttribute('aria-current') === 'true') return;
  const root = option.closest('[data-cart-root]');
  const line = option.closest('.cart-item');
  const previous = line.querySelector('[data-cart-swap][aria-current="true"]');

  // Show the new size right away; the server confirms in the background.
  const showSize = (button) => {
    line.querySelectorAll('[data-cart-swap]').forEach((other) => {
      const current = other === button;
      other.classList.toggle('is-current', current);
      if (current) other.setAttribute('aria-current', 'true');
      else other.removeAttribute('aria-current');
    });
    const size = line.querySelector('[data-line-size]');
    if (size && button) size.textContent = `${root.dataset.sizeSystem} ${button.textContent.trim()}`;
  };
  showSize(option);
  root.querySelector('[data-cart-error]')?.setAttribute('hidden', '');

  try {
    await trackCart(async () => {
      const added = await fetch(`${root.dataset.cartAddUrl}.js`, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ items: [{ id: Number(option.dataset.cartSwap), quantity: Number(line.dataset.quantity) || 1 }] }),
      });
      const addData = await added.json();
      if (!added.ok) throw new Error(addData.description || root.dataset.error);

      const removed = await fetch(`${root.dataset.cartChangeUrl}.js`, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ id: line.dataset.key, quantity: 0, sections: cartSections() }),
      });
      const data = await removed.json();
      if (!removed.ok) throw new Error(data.description || root.dataset.error);
      applySections(data.sections, root);
    });
  } catch (error) {
    if (previous) showSize(previous);
    showCartError(root, error.message || root.dataset.error);
  }
});

/* ---------- Discount codes ---------- */

// Codes go to /cart/update.js; the cart answers with the codes it accepted and the new totals.
function sendDiscounts(root, codes, tried) {
  return trackCart(() => sendDiscountsNow(root, codes, tried));
}

async function sendDiscountsNow(root, codes, tried) {
  const box = root.querySelector('[data-cart-discount]');
  const error = box?.querySelector('[data-cart-discount-error]');
  const button = box?.querySelector('.cart-discount__apply');
  const url = `${root.dataset.cartUpdateUrl}.js`;
  if (error) error.hidden = true;
  button?.setAttribute('aria-busy', 'true');
  try {
    // Wait for quantity changes still on their way, so the totals are right.
    await cartQueue;
    const response = await fetch(url, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ discount: codes.join(','), sections: cartSections() }),
    });
    let data = await response.json();
    if (!response.ok) throw new Error(data.description || root.dataset.error);
    const accepted = (data.discount_codes || []).filter((code) => code.applicable).map((code) => code.code.toLowerCase());
    const rejected = Boolean(tried) && !accepted.includes(tried.toLowerCase());
    if (rejected) {
      // Keep the cart clean: drop the code that didn't apply, then explain.
      const undo = await fetch(url, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ discount: codes.filter((code) => code !== tried).join(','), sections: cartSections() }),
      });
      if (undo.ok) data = await undo.json();
    }
    applySections(data.sections, root);
    const fresh = root.querySelector('[data-cart-discount]');
    if (rejected) {
      const toggle = fresh?.querySelector('[data-cart-discount-toggle]');
      if (toggle) toggle.open = true;
      const message = fresh?.querySelector('[data-cart-discount-error]');
      if (message) {
        message.textContent = root.dataset.discountInvalid;
        message.hidden = false;
      }
      const input = fresh?.querySelector('[name="discount"]');
      if (input) input.value = tried;
    }
  } catch (err) {
    if (error) {
      error.textContent = err.message || root.dataset.error;
      error.hidden = false;
    }
  } finally {
    button?.removeAttribute('aria-busy');
  }
}

const appliedCodes = (box) => (box?.dataset.codes || '').split(',').filter(Boolean);

document.addEventListener('submit', (event) => {
  const form = event.target.closest?.('[data-cart-discount-form]');
  if (!form) return;
  event.preventDefault();
  const root = form.closest('[data-cart-root]');
  const code = form.elements.discount.value.trim();
  if (!root || !code) return;
  const codes = appliedCodes(form.closest('[data-cart-discount]'));
  if (codes.some((c) => c.toLowerCase() === code.toLowerCase())) return;
  sendDiscounts(root, [...codes, code], code);
});

document.addEventListener('click', (event) => {
  const remove = event.target.closest('[data-cart-discount-remove]');
  if (!remove) return;
  const root = remove.closest('[data-cart-root]');
  const box = root?.querySelector('[data-cart-discount]');
  if (!root) return;
  remove.closest('.cart-discount__code, .cart-drawer__discount-row')?.setAttribute('hidden', '');
  sendDiscounts(root, appliedCodes(box).filter((code) => code !== remove.dataset.cartDiscountRemove));
});

/* ---------- Add to cart: the drawer opens right away ---------- */

// The product page knows everything about the pair, so the line can be drawn before the server answers
// (~0.6 s for /cart/add). The server's version replaces it as soon as it arrives.
const escapeHtml = (text) => String(text ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

function optimisticInfo(form) {
  const data = form.closest('product-page')?.data;
  const id = Number(new FormData(form).get('id'));
  // "You might also like" in the cart: the form itself carries the details.
  if (!data && form.dataset.title && id) {
    const option = form.querySelector(`option[value="${id}"]`);
    const cents = Number(option?.dataset.cents ?? form.dataset.cents);
    const compare = Number(option?.dataset.compareCents ?? form.dataset.compareCents) || 0;
    if (!Number.isFinite(cents)) return null;
    const system = cartDrawer()?.dataset.sizeSystem || 'EU';
    return {
      id,
      title: form.dataset.title,
      url: form.dataset.url,
      // The card's photo is already loaded: reuse it so the line shows its picture at once.
      image: form.closest('.cart-suggestion')?.querySelector('.cart-suggestion__media img')?.currentSrc || form.dataset.image,
      upper: escapeHtml(form.dataset.upper),
      editable: form.hasAttribute('data-editable') && !!option,
      size: option ? `${system} ${option.textContent.trim()}` : '',
      cents,
      compareCents: compare > cents ? compare : 0,
    };
  }
  const variant = data?.variants?.find((v) => v.id === id);
  if (!variant || typeof variant.cents !== 'number') return null;
  const size = variant.options?.[data.sizeIndex || 0];
  return {
    id,
    upper: data.upper || '',
    editable: (data.variants?.length || 0) > 1 && !!size,
    max: variant.tracked === 'shopify' && variant.policy === 'deny' ? Math.max(Number(variant.stock) || 0, 0) : 0,
    title: data.title,
    url: data.url,
    image: variant.image || data.image,
    size: size ? `${data.system} ${String(size).split('-')[0]}` : '',
    cents: variant.cents,
    compareCents: variant.compareCents > variant.cents ? variant.compareCents : 0,
  };
}

function showOptimistic(drawer, info) {
  const escape = (text) => String(text ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  const qtyLabel = (n) => drawer.dataset.qtyLabel.replace('__QTY__', n);
  let list = drawer.querySelector('.cart-drawer__items');
  const existing = list?.querySelector(`.cart-item[data-key^="${info.id}:"]`);
  let quantity = 1;

  if (existing) {
    quantity = (Number(existing.dataset.quantity) || 0) + 1;
    setLineQuantity(drawer, existing, quantity);
    existing.querySelector('[data-line-price]')?.replaceChildren(formatMoney(quantity * info.cents));
    list.prepend(existing);
  } else {
    const line = document.createElement('li');
    line.className = 'cart-item is-pending';
    line.dataset.key = `${info.id}:pending`;
    line.dataset.quantity = '1';
    line.dataset.unitPrice = String(info.cents);
    line.innerHTML = `
      <a href="${escape(info.url)}" class="cart-item__media" tabindex="-1" aria-hidden="true">${info.image ? `<img src="${escape(info.image)}" width="100" alt="">` : ''}</a>
      <div class="cart-item__details">
        <a href="${escape(info.url)}" class="cart-item__title">${escape(info.title)}</a>
        ${info.upper ? `<p class="cart-item__variant">${info.upper}</p>` : ''}
        <p class="cart-item__meta">${info.size ? `<span data-line-size>${escape(info.size)}</span> · ` : ''}<span data-line-qty>${escape(qtyLabel(1))}</span>${info.editable && drawer.dataset.editSize ? ` · <button type="button" class="cart-item__edit" tabindex="-1">${escape(drawer.dataset.editSize)} ↓</button>` : ''}</p>
        <p class="cart-item__price"><span class="${info.compareCents ? 'is-sale' : ''}" data-line-price>${escape(formatMoney(info.cents))}</span>${info.compareCents ? `<s class="cart-item__compare">${escape(formatMoney(info.compareCents))}</s>` : ''}</p>
        <div class="cart-item__actions" aria-hidden="true">
          <div class="quantity"><button type="button" class="quantity__button" tabindex="-1">−</button><span class="quantity__value">1</span><button type="button" class="quantity__button" tabindex="-1">+</button></div>
          <button type="button" class="cart-item__remove" tabindex="-1" aria-label="${escape(drawer.dataset.removeLabel || '')}">${drawer.querySelector('template[data-cart-remove-icon]')?.innerHTML || '×'}</button>
        </div>
      </div>`;
    if (!list) {
      // Empty cart: draw the full cart (shipping bar, suggestions, checkout) from the ready template.
      const empty = drawer.querySelector('.cart-drawer__empty');
      const skeleton = drawer.querySelector('template[data-cart-skeleton]');
      if (!empty || !skeleton) return;
      const body = skeleton.content.firstElementChild.cloneNode(true);
      body.querySelectorAll(`.cart-suggestion form [value="${info.id}"]`).forEach((field) => field.closest('.cart-suggestion')?.remove());
      empty.replaceWith(body);
      skeleton.remove();
      list = body.querySelector('.cart-drawer__items');
    }
    list.prepend(line);
  }
  list.scrollTop = 0;

  const addedBox = drawer.querySelector('[data-cart-added]');
  if (addedBox) {
    const title = addedBox.querySelector('.cart-drawer__added-title');
    if (title && drawer.dataset.addedTitle) title.textContent = drawer.dataset.addedTitle;
    addedBox.querySelector('[data-cart-added-text]').textContent = [info.title, info.size, qtyLabel(quantity)].filter(Boolean).join(' · ');
    addedBox.hidden = false;
  }
  recalcRoot(drawer);
  drawer.classList.add('is-optimistic');
}

// Adds go to the server one after another, and only the answer to the last one is drawn,
// so fast clicks (1, 2, 3) never jump back to an older number.

document.addEventListener('submit', async (event) => {
  const form = event.target;
  const drawer = cartDrawer();
  if (!drawer || !(form instanceof HTMLFormElement) || !form.action.includes('/cart/add')) return;
  event.preventDefault();

  const info = optimisticInfo(form);
  // Already holding all the stock there is: say so in the cart instead of asking the server.
  if (info?.max) {
    const lines = [...drawer.querySelectorAll(`.cart-item:not(.is-removing)[data-key^="${info.id}:"]`)];
    const inCart = lines.reduce((sum, line) => sum + (Number(line.dataset.quantity) || 0), 0);
    if (lines.length && inCart + 1 > info.max) {
      form.querySelector('[data-form-error]')?.remove();
      showStockNotice(drawer, lines[0], info.max);
      drawer.open();
      return;
    }
  }
  const submit = form.querySelector('[type="submit"]');
  const suggestion = info ? form.closest('.cart-suggestion') : null;
  if (suggestion) suggestion.hidden = true;
  // With the instant line the button stays usable, so another click adds one more right away.
  if (!info) {
    submit?.setAttribute('aria-busy', 'true');
    submit?.setAttribute('disabled', '');
  }
  form.querySelector('[data-form-error]')?.remove();

  // Open immediately. From the product page the pair is drawn at once; elsewhere the loading line runs.
  drawer.classList.add('is-adding');
  const addedBox = drawer.querySelector('[data-cart-added]');
  if (info) {
    showOptimistic(drawer, info);
  } else if (addedBox) {
    addedBox.querySelector('[data-cart-added-text]').textContent = drawer.dataset.adding || '';
    addedBox.hidden = false;
  }
  drawer.open();

  const body = new FormData(form);
  body.append('sections', cartSections().join(','));
  queuedCartOps += 1;
  syncCheckoutBusy();

  try {
    const request = cartQueue.then(async () => {
      const response = await fetch(`${drawer.dataset.cartAddUrl}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.description || drawer.dataset.error);
      return json;
    });
    // A failed add must not block the cart actions queued after it.
    cartQueue = request.catch(() => {});
    const data = await request;
    // The drawer is the origin: its lines get the light patch, so nothing already on screen is redrawn.
    if (queuedCartOps === 1 && pendingLines.size === 0) applySections(data.sections, drawer, data.items ? data.items[data.items.length - 1] : data);
  } catch (error) {
    if (addedBox) addedBox.hidden = true;
    const hadItems = !!drawer.querySelector('.cart-item:not(.is-pending)');
    // Undo the instant line: show the server's real cart again.
    if (info) await refreshCart();
    if (hadItems) {
      if (suggestion) suggestion.hidden = false;
      const line = info && drawer.querySelector(`.cart-item[data-key^="${info.id}:"]`);
      if (line && Number(line.dataset.max)) showStockNotice(drawer, line, Number(line.dataset.max));
      else showCartError(drawer, error.message || drawer.dataset.error);
      return;
    }
    drawer.close();
    if (suggestion) suggestion.hidden = false;
    const message = document.createElement('p');
    message.className = 'form-error';
    message.dataset.formError = '';
    message.setAttribute('role', 'alert');
    message.textContent = error.message || drawer.dataset.error;
    form.append(message);
  } finally {
    queuedCartOps -= 1;
    syncCheckoutBusy();
    if (queuedCartOps === 0) drawer.classList.remove('is-adding', 'is-optimistic');
    submit?.removeAttribute('aria-busy');
    submit?.removeAttribute('disabled');
  }
});
